import type { IComponentController, IScope } from "angular";
import "angular-translate";
import { catchError, concatMap, finalize, map } from "rxjs/operators";
import type { Observable } from "rxjs";
import { throwError as observableThrowError } from "rxjs";
import { clone, cloneDeep, some } from "lodash";
import "@progress/kendo-ui/js/kendo.autocomplete";

import type { IAccountRepository } from "App/Modules/Auth";
import type {
    IEnumService,
    ILabel,
    IPartnerUserFlat,
    IPartnerUserInsert,
    IPartnerUserSlim,
    IResponse,
    IResponseWithLoading,
    IRole,
    ISite,
    ITeamMember,
} from "App/Modules/Models";
import { RoleFunctions } from "App/Modules/Models";
import type { IPartnerUsersRepository, IRolesRepository, ISitesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";

enum AddUserDialogStates {
    SelectUser,
    DefineUserParameters,
    DefineUserRole,
}

export default class AddUserToSiteComponentController implements IComponentController {
    public static $inject: string[] = [
        "$scope",
        "$translate",
        "mmSitesRepository",
        "mmPartnerUsersRepository",
        "irRolesRepository",
        "mmEnumService",
        "mmAccountRepository",
    ];

    public partnerId: string;

    public site: ISite;

    public state: AddUserDialogStates = AddUserDialogStates.SelectUser;

    public isNew: boolean;

    public user: IPartnerUserSlim;

    public originalUser: IPartnerUserSlim;

    public errorMessage: string;

    public numberPattern = "\\+?[\\d ]+";

    public addResponse: IResponseWithLoading<ITeamMember>;

    public createLogin: boolean;

    public enableTwoFactorAuth: boolean;

    public siteRoles: IRole[];

    public partnerUserRole: IRole;

    public labels: string[];

    public partnerUserLabelsString: string;

    public resolve: IFormContainerResolve<ITeamMember, ISite>;

    public siteRolesResponse: IResponseWithLoading<IRole[]>;

    public siteUsersResponse: IResponseWithLoading<ITeamMember[]>;

    public siteLabelsResponse: IResponseWithLoading<ILabel[]>;

    public existingUsers: string[] = [];

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly sitesRepository: ISitesRepository,
        private readonly partnerUsersRepository: IPartnerUsersRepository,
        private readonly rolesRepository: IRolesRepository,
        private readonly enumService: IEnumService,
        private readonly accountRepository: IAccountRepository,
    ) {}

    public $onInit() {
        this.site = this.resolve.bindings;
        this.partnerId = this.resolve.bindings.partner.id;
        this.enableTwoFactorAuth = false;

        extendWithLoadingAndFail(this.sitesRepository.getUsersForSite(this.site.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.siteUsersResponse = response;
                    if (response.isSuccessful) {
                        const ids = response.data.map((x) => x.partnerUser.id);
                        this.existingUsers = ids;
                    }
                },
                (error) => {
                    this.siteUsersResponse = error;
                },
            );

        extendWithLoadingAndFail(this.rolesRepository.getSiteRoles())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                this.siteRolesResponse = response;
                if (response.isSuccessful) {
                    this.partnerUserRole = this.rolesRepository.getNoRole();
                    this.siteRoles = [this.partnerUserRole, ...response.data];
                }
            });

        extendWithLoadingAndFail(this.partnerUsersRepository.getLabels(this.partnerId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                this.siteLabelsResponse = response;
                if (response.isSuccessful) {
                    this.labels = response.data.map((l) => l.label);
                }
            });
    }

    public isLoading(): boolean {
        return some(
            [this.siteUsersResponse, this.siteLabelsResponse, this.siteRolesResponse],
            (item) => item.isLoading === true,
        );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public sortById(role: IRole) {
        return RoleFunctions.sortById(role);
    }

    public newUser(searchString: string) {
        this.isNew = true;
        this.user = {} as IPartnerUserSlim;

        const isEmail = /^[\w+.-]+@[\d.A-Za-z-]+$/;
        const isPhoneNumber = /^[\d +.-]+$/;

        if (isEmail.test(searchString)) {
            this.user.email = searchString;
        } else if (isPhoneNumber.test(searchString)) {
            this.user.phoneNumber = searchString;
            this.user.smsNumber = searchString;
        } else {
            this.user.fullName = searchString;
        }
        this.state = AddUserDialogStates.DefineUserParameters;
    }

    public userSelected(user: IPartnerUserSlim) {
        this.isNew = false;
        this.originalUser = cloneDeep(user);
        this.user = user;
        this.state = AddUserDialogStates.DefineUserParameters;
    }

    public next() {
        this.state = AddUserDialogStates.DefineUserRole;
    }

    public add() {
        let userObservable: Observable<IResponse<IPartnerUserSlim>>;

        if (this.isNew) {
            userObservable = this.createUser(this.user);
        } else {
            userObservable = this.patchUser(this.user);
        }

        extendWithLoadingAndFail(
            userObservable.pipe(
                concatMap((userResponse: IResponse<IPartnerUserSlim>) => {
                    const teamMember: any = {
                        labels: null,
                        notificationSubscriptions: null,
                        partnerUser: {
                            id: userResponse.data.id,
                        },
                        role:
                            this.partnerUserRole && this.partnerUserRole.id
                                ? {
                                      id: this.partnerUserRole.id,
                                  }
                                : null,
                    };

                    if (this.partnerUserLabelsString) {
                        teamMember.labels = this.partnerUserLabelsString
                            .split(",")
                            .map((l) => l.trim())
                            .filter((l) => l !== "")
                            .map((l) => ({ label: l }));
                    }

                    return this.sitesRepository.addUserToSite(this.site.id, teamMember);
                }),
            ),
        ).subscribe(
            (response: IResponseWithLoading<ITeamMember>) => {
                this.addResponse = response;
                if (!response.isLoading && response.data) {
                    this.existingUsers.push(response.data.partnerUser.id);
                    this.resolve.options.onOk(response.data);
                }
            },
            (error) => {
                this.addResponse = error;
            },
        );
    }

    private createUser(user: IPartnerUserSlim): Observable<IResponse<IPartnerUserSlim>> {
        const partnerUserInsert = {
            displayName: user.fullName,
            email: user.email,
            enableTwoFactorAuth: this.enableTwoFactorAuth,
            fullName: user.fullName,
            partnerId: this.site.partner.id,
            phoneNumber: user.phoneNumber,
            smsNumber: user.smsNumber,
            userId: user.id,
        } as IPartnerUserInsert;

        let result = this.partnerUsersRepository.addPartnerUser(this.partnerId, partnerUserInsert).pipe(
            catchError((err: IResponse<void>) => {
                err.error = {
                    // Does not follow typings
                    message: "Failed to create user",
                    serverError: err.error,
                } as any;
                return observableThrowError(err);
            }),
        );

        if (this.createLogin) {
            result = result.pipe(
                concatMap((pu: IResponse<IPartnerUserSlim>) =>
                    this.partnerUsersRepository.verify(this.partnerId, pu.data.id, this.enableTwoFactorAuth).pipe(
                        catchError((err: IResponse<void>) => {
                            err.error = {
                                // Does not follow typings
                                message: "Failed to verify user",
                                serverError: err.error,
                            } as any;
                            return observableThrowError(err);
                        }),
                        map(() => pu),
                    ),
                ),
            );

            if (this.enableTwoFactorAuth) {
                result = result.pipe(
                    concatMap((pu: IResponse<IPartnerUserSlim>) =>
                        this.accountRepository.twoFactorAuthentication(pu.data.email).pipe(
                            catchError((err: IResponse<void>) => {
                                err.error = {
                                    // Does not follow typings
                                    message: "Failed to create user",
                                    serverError: err.error,
                                } as any;
                                return observableThrowError(err);
                            }),
                            map(() => pu),
                        ),
                    ),
                );
            }
        }

        return result;
    }

    private patchUser(user: IPartnerUserSlim): Observable<IResponse<IPartnerUserSlim>> {
        const patchObject = {} as IPartnerUserFlat;
        if (this.originalUser.phoneNumber !== user.phoneNumber) {
            patchObject.phoneNumber = user.phoneNumber;
        }
        if (this.originalUser.smsNumber !== user.smsNumber) {
            patchObject.smsNumber = user.smsNumber;
        }

        return this.partnerUsersRepository.patchPartnerUser(user.partner.id, user.id, patchObject).pipe(
            map((response: IResponse<IPartnerUserFlat>) => {
                user.phoneNumber = response.data.phoneNumber;
                user.smsNumber = response.data.smsNumber;
                const userResponse = clone(response) as any as IResponse<IPartnerUserSlim>;
                userResponse.data = user;
                return userResponse;
            }),
        );
    }
}
